"use client";

import { useMemo } from "react";

import { useWidgetSamples } from "@/hooks/useWidgetData";

import { WidgetChrome } from "./WidgetChrome";
import type { WidgetShellProps } from "./types";

function formatCell(v: unknown): string {
  if (v == null) {
    return "—";
  }
  if (typeof v === "number") {
    return Number.isFinite(v) ? v.toLocaleString(undefined, { maximumFractionDigits: 4 }) : "—";
  }
  if (typeof v === "string") {
    return v;
  }
  try {
    return JSON.stringify(v).slice(0, 96);
  } catch {
    return "—";
  }
}

export function TableWidget({ widget, config, timeRange, customRange }: WidgetShellProps) {
  const channelKey = config.channel ?? "";
  const { rows, isLoading, error } = useWidgetSamples({
    deviceId: config.device_id,
    channel: channelKey,
    timeRange,
    customRange,
    aggregation: config.aggregation,
    refreshIntervalSeconds: config.refresh_interval_seconds,
    enabled: Boolean(channelKey),
  });

  const title = config.title?.trim() || config.channel || "Table";

  const tableRows = useMemo(() => {
    if (!channelKey) {
      return [];
    }
    return [...rows].reverse().slice(0, 12).map((r) => ({
      time: r.time,
      value: r.data?.[channelKey],
    }));
  }, [rows, channelKey]);

  const configError =
    !channelKey && (config.formula || config.metric_id)
      ? new Error("Table widgets require a raw channel. Use a line chart or stat for derived metrics.")
      : null;

  return (
    <WidgetChrome
      widget={widget}
      title={title}
      subtitle="Recent samples"
      isLoading={isLoading && rows.length === 0}
      error={configError ?? error}
    >
      {!configError && !error && tableRows.length === 0 && !isLoading ? (
        <p className="text-caption text-muted">No samples in this window.</p>
      ) : !configError && !error ? (
        <div className="-mx-ds-1 max-h-[16rem] overflow-auto rounded-xl border border-border/35">
          <table className="w-full min-w-[12rem] border-collapse text-caption">
            <thead className="sticky top-0 z-[1] bg-card/95 backdrop-blur-sm">
              <tr className="text-left text-muted">
                <th className="px-ds-3 py-ds-2 font-medium">Time</th>
                <th className="px-ds-3 py-ds-2 font-medium">{channelKey || "—"}</th>
              </tr>
            </thead>
            <tbody>
              {tableRows.map((row, i) => (
                <tr key={`${row.time}-${i}`} className="border-t border-border/30">
                  <td className="whitespace-nowrap px-ds-3 py-ds-2 tabular-nums text-muted">
                    {new Date(row.time).toLocaleString(undefined, {
                      month: "short",
                      day: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </td>
                  <td className="px-ds-3 py-ds-2 font-medium text-foreground">{formatCell(row.value)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </WidgetChrome>
  );
}
